/**
 * Parse specs and classify cell values for in-process inspection (Phase 1).
 */

import { formatSpecificationDisplay } from './specificationDisplay';

const TOL_RE = /([\d.]+)\s*[±\+\-]\s*([\d.]+)/;

export function parseNominalTolerance(specString) {
  const s = formatSpecificationDisplay(specString || '').trim();
  const m = s.match(TOL_RE);
  if (!m) return null;
  const nominal = parseFloat(m[1]);
  const tol = parseFloat(m[2]);
  if (Number.isNaN(nominal) || Number.isNaN(tol)) return null;
  return { nominal, tolerance: tol, min: nominal - tol, max: nominal + tol };
}

/** multi5 row: max absolute value allowed (e.g. 0.05) */
export function parseMaxAbs(specString) {
  const s = (specString || '').trim();
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return n;
}

export function parseMulti5Values(raw) {
  const t = (raw || '').trim();
  if (!t) return [];
  return t.split(/[,，]/).map((x) => x.trim()).filter(Boolean).map((x) => parseFloat(x));
}

/**
 * @returns {'empty'|'pass'|'fail'|'neutral'}
 */
export function classifyCell(rowType, resolvedSpec, rawValue) {
  const v = rawValue != null ? String(rawValue).trim() : '';
  if (!v) return 'empty';

  if (rowType === 'numeric_tol') {
    const tol = parseNominalTolerance(resolvedSpec);
    if (!tol) return 'neutral';
    const n = parseFloat(v.replace(/,/g, ''));
    if (Number.isNaN(n)) return 'fail';
    const eps = 1e-9;
    const { min, max, nominal, tolerance } = tol;
    // Full measured value in spec band (existing behaviour).
    if (n >= min - eps && n <= max + eps) return 'pass';
    // Signed delta from nominal, e.g. spec 32 ± 0.5 and entry "-0.2" → 31.8.
    const fromSignedDelta = nominal + n;
    if (fromSignedDelta >= min - eps && fromSignedDelta <= max + eps) return 'pass';
    // Delta magnitude only, e.g. entry "0.2" meaning within ±0.5 of nominal.
    if (Math.abs(n) <= tolerance + eps) return 'pass';
    return 'fail';
  }

  if (rowType === 'multi5') {
    const maxAbs = parseMaxAbs(resolvedSpec);
    if (maxAbs == null) return 'neutral';
    const nums = parseMulti5Values(v);
    if (nums.length === 0) return 'empty';
    const ok = nums.every((x) => !Number.isNaN(x) && Math.abs(x) <= maxAbs + 1e-9);
    return ok ? 'pass' : 'fail';
  }

  if (rowType === 'dropdown') {
    const s = v.trim();
    if (!s) return 'empty';
    const upper = s.toUpperCase();
    if (upper === 'NOT OK' || s === 'Not Available') return 'fail';
    if (upper === 'ADJUST') return 'neutral';
    return 'pass';
  }

  return 'neutral';
}
